import { isNotDefined } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import classNames from 'classnames';
import { PercentOrAmountValue } from 'components/secondaryMarket/live/sidePanel/quoted/greeks/Cell';
import { SkeletonWrapper } from 'components/shared/SkeletonWrapper';
import { TickingValue } from 'components/shared/TickingValue/TickingValue';
import { useSPIntl } from 'hooks/useIntl';
import { FormattedMessage } from 'react-intl';
import type { SecondaryMarketProductGreeks } from 'state/secondaryMarket/productSearch';

const getPerformanceClass = (performance?: number) => {
  if (isNotDefined(performance)) {
    return '';
  } else if (performance >= 0) {
    return 'text-success';
  } else {
    return 'text-danger';
  }
};
export function GreeksRow({
  delta,
  gamma,
  performance,
  spot,
  spotDate,
  theta,
  label,
  bloombergTicker,
  currency,
  underlyingBcsKey,
  underlyingInitialPrice,
  vega,
  vol,
  isWorst,
  isLoadingGreeks,
  hasSpotDate,
  greeksPrecision,
}: SecondaryMarketProductGreeks & {
  greeksPrecision: number;
  hasSpotDate?: boolean;
  isLoadingGreeks: boolean;
}): React.JSX.Element {
  const { formatDisplayedDate } = useSPIntl();
  return (
    <tr key={underlyingBcsKey}>
      <td>
        <SkeletonWrapper isLoading={isLoadingGreeks} width="8em">
          <div
            className={classNames('p-0 m-0 d-flex align-items-center justify-content-start', {
              ['fw-bold']: isWorst,
            })}
          >
            <Tooltip position="top" contents={label}>
              <div className="text-truncate col-11 m-0 p-0">{label}</div>
            </Tooltip>
            {isWorst && (
              <div className="col-1 p-0">
                <Tooltip position="top" contents={<FormattedMessage id="secondaryMarket.products.greeks.worstOf" />}>
                  <i className="icon text-info fs-12">fiber_manual_record</i>
                </Tooltip>
              </div>
            )}
          </div>
        </SkeletonWrapper>
      </td>
      <td>
        <SkeletonWrapper isLoading={isLoadingGreeks} width="8em">
          {!!bloombergTicker && (
            <div className="badge badge-outline-secondary rounded-pill">
              <span className="text-primary">{bloombergTicker}</span>
            </div>
          )}
        </SkeletonWrapper>
      </td>
      <td className="text-end">
        <SkeletonWrapper isLoading={isLoadingGreeks} width="8em">
          <TickingValue value={spot}>
            <PercentOrAmountValue amount={spot} greeksPrecision={greeksPrecision} />
          </TickingValue>
        </SkeletonWrapper>
      </td>
      <td>
        <SkeletonWrapper isLoading={isLoadingGreeks} width="8em">
          {currency}
        </SkeletonWrapper>
      </td>
      <td
        className={classNames(
          underlyingInitialPrice !== undefined ? getPerformanceClass(performance) : undefined,
          'text-end',
        )}
      >
        <SkeletonWrapper isLoading={isLoadingGreeks} width="8em">
          <PercentOrAmountValue
            percent={underlyingInitialPrice !== undefined ? performance : undefined}
            greeksPrecision={2}
            isAlreadyPercent
          />
        </SkeletonWrapper>
      </td>
      <td className="text-end">
        <SkeletonWrapper isLoading={isLoadingGreeks} width="8em">
          <PercentOrAmountValue amount={underlyingInitialPrice} greeksPrecision={greeksPrecision} />
        </SkeletonWrapper>
      </td>
      <td className="text-end">
        <SkeletonWrapper isLoading={isLoadingGreeks} width="8em">
          <PercentOrAmountValue percent={delta} greeksPrecision={greeksPrecision} isAlreadyPercent />
        </SkeletonWrapper>
      </td>
      <td className="text-end">
        <SkeletonWrapper isLoading={isLoadingGreeks} width="8em">
          <PercentOrAmountValue amount={gamma} greeksPrecision={greeksPrecision} />
        </SkeletonWrapper>
      </td>
      <td className="text-end">
        <SkeletonWrapper isLoading={isLoadingGreeks} width="8em">
          <PercentOrAmountValue amount={theta} greeksPrecision={greeksPrecision} />
        </SkeletonWrapper>
      </td>
      <td className="text-end">
        <SkeletonWrapper isLoading={isLoadingGreeks} width="8em">
          <PercentOrAmountValue amount={vega} greeksPrecision={greeksPrecision} />
        </SkeletonWrapper>
      </td>
      <td className="text-end">
        <SkeletonWrapper isLoading={isLoadingGreeks} width="8em">
          <PercentOrAmountValue amount={vol} greeksPrecision={greeksPrecision} />
        </SkeletonWrapper>
      </td>
      {hasSpotDate && (
        <td>
          <SkeletonWrapper isLoading={isLoadingGreeks} width="8em">
            {formatDisplayedDate(spotDate)}
          </SkeletonWrapper>
        </td>
      )}
    </tr>
  );
}
